import { useState, useEffect, useRef } from 'react';
import { Flex, Box, Input, Textarea } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { LuClock, LuMapPin, LuWifi } from 'react-icons/lu';
import { FaInstagram } from 'react-icons/fa';
import { updateMenu, type MenuResponse } from '../../api/menus';
import { toast } from 'react-toastify';
import ModalShell, { CancelButton, SaveButton, inputStyles } from './ModalShell';

interface StoreInfoModalProps {
  open: boolean;
  onClose: () => void;
  menu: MenuResponse;
  onMenuUpdate: (menu: MenuResponse) => void;
}

const StoreInfoModal = ({ open, onClose, menu, onMenuUpdate }: StoreInfoModalProps) => {
  const { t } = useTranslation();
  const [storeHours, setStoreHours] = useState('');
  const [storeAddress, setStoreAddress] = useState('');
  const [addressUrl, setAddressUrl] = useState('');
  const [wifiPassword, setWifiPassword] = useState('');
  const [instagram, setInstagram] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const prevOpen = useRef(false);

  useEffect(() => {
    if (open && !prevOpen.current) {
      setStoreHours(menu.store_info?.store_hours || '');
      setStoreAddress(menu.store_info?.address || '');
      setAddressUrl(menu.store_info?.address_url || '');
      setWifiPassword(menu.store_info?.wifi_password || '');
      setInstagram(menu.store_info?.instagram || '');
    }
    prevOpen.current = open;
  }, [open, menu]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updated = await updateMenu(menu.id, {
        store_info: {
          store_hours: storeHours || undefined,
          address: storeAddress || undefined,
          address_url: addressUrl || undefined,
          wifi_password: wifiPassword || undefined,
          instagram: instagram || undefined,
        },
      });
      onMenuUpdate(updated);
      toast.success(t('menus.menuUpdated'));
      onClose();
    } catch (error) {
      toast.error(t('menus.updateFailed'));
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const iconRow = (icon: React.ReactNode, child: React.ReactNode, alignTop = false) => (
    <Flex alignItems={alignTop ? 'flex-start' : 'center'} gap="12px">
      <Box color="neutral.400" flexShrink={0} {...(alignTop ? { mt: '12px' } : {})}>
        {icon}
      </Box>
      {child}
    </Flex>
  );

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={t('menus.storeInfo', 'Store Info')}
      footer={
        <>
          <CancelButton onClick={onClose} label={t('common.cancel')} />
          <SaveButton onClick={handleSave} loading={isSaving} label={t('common.save')} />
        </>
      }
    >
      <Flex direction="column" rowGap="16px">
        {iconRow(
          <LuClock style={{ width: 18, height: 18 }} />,
          <Textarea
            value={storeHours}
            onChange={(e) => setStoreHours(e.target.value)}
            placeholder={t('menus.storeHoursPlaceholder', 'Mon-Fri: 9am-10pm\nSat-Sun: 10am-11pm')}
            {...inputStyles}
            rows={7}
            flex="1"
          />,
          true,
        )}
        <Flex alignItems="flex-start" gap="12px">
          <Box color="neutral.400" flexShrink={0} mt="12px">
            <LuMapPin style={{ width: 18, height: 18 }} />
          </Box>
          <Flex direction="column" gap="8px" flex="1">
            <Input
              value={storeAddress}
              onChange={(e) => setStoreAddress(e.target.value)}
              placeholder={t('menus.addressPlaceholder', '123 Main St, New York, NY 10001')}
              {...inputStyles}
            />
            <Input
              value={addressUrl}
              onChange={(e) => setAddressUrl(e.target.value)}
              placeholder={t('menus.addressUrlPlaceholder', 'Google Maps link')}
              {...inputStyles}
              fontSize="13px"
            />
          </Flex>
        </Flex>
        {iconRow(
          <LuWifi style={{ width: 18, height: 18 }} />,
          <Input
            value={wifiPassword}
            onChange={(e) => setWifiPassword(e.target.value)}
            placeholder={t('menus.wifiPlaceholder', 'Wifi password')}
            {...inputStyles}
            flex="1"
          />,
        )}
        {iconRow(
          <FaInstagram style={{ width: 18, height: 18 }} />,
          <Input
            value={instagram}
            onChange={(e) => setInstagram(e.target.value)}
            placeholder={t('menus.instagramPlaceholder', '@myrestaurant')}
            {...inputStyles}
            flex="1"
          />,
        )}
      </Flex>
    </ModalShell>
  );
};

export default StoreInfoModal;
